import { useMutation, useQuery } from "@apollo/client";
import { Typography } from "@mui/material";
import { Grid, Stack } from "@mui/system";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { DataGrid } from "components/DataGrid";
import {
  documentKindNames,
  manifestNumberLabel,
} from "encamp-shared/src/waste/shippingDocumentLabels";
import { ProcessedManifestsQuery } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useLocation, useNavigate } from "react-router-dom";

import { WASTE_LINES_UNIFIED_QUERY } from "../ManifestLineItems/api";
import { REVERT_VENDOR_MANIFEST_AND_LINE_ITEMS_MUTATION } from "./api";
import { newDocumentSentence } from "./editManifestSuccessMessage";

type Manifest = ProcessedManifestsQuery["processedManifests"]["items"][number];

interface RevertManifestDialogProps {
  manifest: Manifest;
  onClose: () => void;
}

export const RevertManifestDialog = ({ manifest, onClose }: RevertManifestDialogProps) => {
  const alerts = useAlerts();
  const wording = documentKindNames(manifest.documentKind, manifest.manifestTrackingNumber);
  const originalNumber =
    manifest.vendorManifest?.originalManifestTrackingNumber ||
    manifest.vendorManifest?.manifestTrackingNumber;
  const numberLabel = manifestNumberLabel({
    documentKind: manifest.documentKind,
    manifestTrackingNumber: originalNumber ?? null,
  });
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: wasteLinesData,
    previousData: previousWasteLinesData,
    loading,
  } = useQuery(WASTE_LINES_UNIFIED_QUERY, {
    variables: {
      search: `tenantId:${tenantId} processedManifestId:${manifest.id}`,
      includeOriginalVendorWasteLine: true,
    },
    skip: !tenantId || !manifest.id,
  });
  const wasteLines =
    (loading
      ? previousWasteLinesData?.wasteLinesUnified?.items
      : wasteLinesData?.wasteLinesUnified?.items) ?? [];

  const [revertManifest, { loading: revertingManifest }] = useMutation(
    REVERT_VENDOR_MANIFEST_AND_LINE_ITEMS_MUTATION,
    {
      refetchQueries: ["ProcessedManifests", "WasteLinesUnified"],
      onCompleted: (data) => {
        const newManifest = data.revertVendorManifestAndLineItems.newProcessedManifest;
        if (newManifest) {
          alerts.success(
            `${wording.sentence} successfully reverted. ${newDocumentSentence(newManifest.manifestTrackingNumber, manifest.documentKind)}`
          );
          // Replace the current manifest ID in the URL with the new manifest ID
          const pathSegments = location.pathname.split("/").filter(Boolean);
          const manifestIndex = pathSegments.findIndex((segment) => segment === manifest.id);
          if (manifestIndex !== -1) {
            pathSegments[manifestIndex] = newManifest.id;
            const newPath = `/${pathSegments.join("/")}${location.search}`;
            navigate(newPath, { replace: true, preventScrollReset: true });
          }
        } else {
          alerts.success(`${wording.sentence} successfully reverted.`);
        }
        onClose();
      },
      onError: (error) => {
        console.error("Error reverting vendor document:", error);

        // Check if this is a duplicate manifest error
        const isDuplicateError = error.graphQLErrors?.some(
          (err) => err.extensions?.code === "DUPLICATE_MANIFEST"
        );

        if (isDuplicateError) {
          // Display the specific error message from the server
          const duplicateError = error.graphQLErrors.find(
            (err) => err.extensions?.code === "DUPLICATE_MANIFEST"
          );
          alerts.error(
            duplicateError?.message ||
              `A ${wording.noun} with the reverted ${wording.numberNoun} already exists.`
          );
        } else {
          // Generic error message for other types of errors
          alerts.error(`Failed to revert ${wording.noun}`);
        }
      },
    }
  );

  const handleRevert = async () => {
    await revertManifest({
      variables: {
        id: manifest.id,
      },
    });
  };

  return (
    <ConfirmDialog
      open
      onConfirm={handleRevert}
      onClose={onClose}
      header={`Are you sure you want to revert this vendor ${wording.noun}?`}
      confirmText="Yes, Revert"
      loading={revertingManifest}
      info={
        <Stack spacing={2}>
          <Grid container columnSpacing={2} rowSpacing={1}>
            <Grid size={3}>
              <Typography variant="caption" color="textSecondary">
                {wording.numberLabel}
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>{numberLabel}</Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="caption" color="textSecondary">
                Facility Name
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>{manifest.facility?.name}</Typography>
            </Grid>
            <Grid size={3}>
              <Typography variant="caption" color="textSecondary">
                EPA ID
              </Typography>
            </Grid>
            <Grid size={9}>
              <Typography>{manifest.epaId}</Typography>
            </Grid>
          </Grid>
          <Typography variant="body1" color="textSecondary">
            Reverting this vendor {wording.noun} will change all data back to how it was originally,
            including line item information, tags, and any Not Reporting designations. This action
            cannot be undone.
          </Typography>
          <DataGrid
            columns={[
              {
                field: "originalVendorProfileDescription",
                valueFormatter: (_, row) => row.originalVendorProfileDescription || row.lineItem,
                headerName: "Line Item",
                flex: 2,
              },
              {
                field: "vendorAmount",
                valueFormatter: (_, row) =>
                  `${(row.originalVendorAmount ?? row.vendorAmount)?.toLocaleString()} lbs`,
                headerName: "Amount (Vendor)",
                flex: 1,
              },
            ]}
            rows={wasteLines}
            loading={loading}
            noRowsMessage="No line items"
          />
        </Stack>
      }
    />
  );
};
